import { useCallback, useMemo, useState } from 'react';
import adminService from '@wlb/shared/api/admin/adminService';
import type { ErrorLogResponse } from '@wlb/shared/api/admin/adminTypes';
import useDebounce from '@wlb/shared/hooks/useDebounce';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { DataTable, type DataTableColumn } from '../components/ui/DataTable';
import { FormError } from '../components/ui/FormError';
import { IconButton } from '../components/ui/IconButton';
import { Pagination } from '../components/ui/Pagination';
import { TextField } from '../components/ui/TextField';
import { PageHeader } from '../components/layout/PageHeader';
import { useAsyncResource } from '../hooks/useAsyncResource';
import {
  dateInputToIsoEnd,
  dateInputToIsoStart,
  daysAgoInputValue,
  formatDateTime,
  isValidDateRange,
  toDateInputValue,
} from '../utils/format';
import '../components/ui/ui.css';
import './pages.css';

const PAGE_SIZE = 20;

export function ErrorLogs() {
  const { t } = useTranslation('admin');
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [startDate, setStartDate] = useState(() => daysAgoInputValue(7));
  const [endDate, setEndDate] = useState(() => toDateInputValue(new Date()));
  const [pageNumber, setPageNumber] = useState(1);
  const debouncedSearch = useDebounce(search);
  const debouncedType = useDebounce(type);

  const load = useCallback(
    () =>
      adminService.getErrorLogs({
        searchKeyword: debouncedSearch.trim(),
        type: debouncedType.trim(),
        startDate: dateInputToIsoStart(startDate),
        endDate: dateInputToIsoEnd(endDate),
        pageNumber,
        pageSize: PAGE_SIZE,
      }),
    [debouncedSearch, debouncedType, endDate, pageNumber, startDate],
  );

  const rangeValid = isValidDateRange(startDate, endDate);

  const requestKey = [
    debouncedSearch,
    debouncedType,
    startDate,
    endDate,
    pageNumber,
  ].join('|');

  const { data, status, refreshing, error, reload } = useAsyncResource(
    load,
    requestKey,
    t('common.errorTitle'),
    rangeValid,
    'errorLogs',
  );

  const columns = useMemo<readonly DataTableColumn<ErrorLogResponse>[]>(
    () => [
      {
        key: 'time',
        header: t('errorLogs.columns.time'),
        render: row => formatDateTime(row.timeUtc) || t('common.none'),
      },
      {
        key: 'type',
        header: t('errorLogs.columns.type'),
        render: row => (
          <span className="ui-table__truncate">{row.type || t('common.none')}</span>
        ),
      },
      {
        key: 'message',
        header: t('errorLogs.columns.message'),
        render: row => (
          <span className="ui-table__truncate">{row.message || t('common.none')}</span>
        ),
      },
      {
        key: 'request',
        header: t('errorLogs.columns.request'),
        render: row => (
          <span className="ui-table__truncate ui-table__mono">
            {[row.requestMethod, row.requestUri].filter(Boolean).join(' ') ||
              t('common.none')}
          </span>
        ),
      },
      {
        key: 'user',
        header: t('errorLogs.columns.user'),
        render: row => row.userName || t('common.none'),
      },
    ],
    [t],
  );

  const resetPage = (apply: () => void) => {
    setPageNumber(1);
    apply();
  };

  return (
    <div className="page page--fill">
      <PageHeader
        title={t('errorLogs.title')}
        subtitle={t('errorLogs.subtitle')}
        actions={
          <IconButton
            icon="refresh"
            tone="slate"
            label={t('common.refresh')}
            disabled={refreshing}
            onClick={reload}
          />
        }
      />

      <section className="page__toolbar" aria-label={t('common.filters')}>
        <div className="page__toolbar-field page__toolbar-field--wide">
          <TextField
            label={t('common.search')}
            type="search"
            placeholder={t('errorLogs.searchPlaceholder')}
            value={search}
            onChange={event => resetPage(() => setSearch(event.target.value))}
          />
        </div>
        <div className="page__toolbar-field">
          <TextField
            label={t('errorLogs.filters.typeLabel')}
            placeholder={t('errorLogs.filters.typePlaceholder')}
            value={type}
            onChange={event => resetPage(() => setType(event.target.value))}
          />
        </div>
        <div className="page__toolbar-field">
          <TextField
            label={t('errorLogs.filters.startDate')}
            type="date"
            value={startDate}
            max={endDate}
            onChange={event => resetPage(() => setStartDate(event.target.value))}
          />
        </div>
        <div className="page__toolbar-field">
          <TextField
            label={t('errorLogs.filters.endDate')}
            type="date"
            value={endDate}
            min={startDate}
            onChange={event => resetPage(() => setEndDate(event.target.value))}
          />
        </div>
      </section>

      <FormError
        variant="banner"
        message={rangeValid ? '' : t('errorLogs.filters.rangeInvalid')}
      />

      <DataTable
        caption={t('errorLogs.title')}
        columns={columns}
        rows={data?.items ?? []}
        rowKey={row => row.errorId}
        status={status}
        refreshing={refreshing}
        error={error}
        onRetry={reload}
        emptyTitle={t('errorLogs.emptyTitle')}
        emptyMessage={t('errorLogs.emptyMessage')}
        scrollResetKey={requestKey}
        footer={
          data ? (
            <Pagination
              pageNumber={data.pageNumber}
              totalPages={data.totalPages}
              totalCount={data.totalCount}
              hasPreviousPage={data.hasPreviousPage}
              hasNextPage={data.hasNextPage}
              pageSize={PAGE_SIZE}
              onChange={setPageNumber}
            />
          ) : null
        }
      />
    </div>
  );
}
